import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { ArrowRight, Clock, RefreshCw, ShieldCheck } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import {
  CoverageNotice,
  EmptyState,
  ErrorState,
  LoadingState,
  StatTile,
  UnavailableNotice,
} from '../components/DataViz'
import { fetchPendingGates, type PendingGate, type PendingGates } from '../lib/api'
import { useAuth } from '../lib/auth'

function waited(since?: string | null): string {
  if (!since) return 'waiting'
  const then = new Date(since).getTime()
  if (Number.isNaN(then)) return 'waiting'
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 48) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

/**
 * Approvals inbox (AFOS-41): every pending named-human gate in the project,
 * oldest first, each linked to the page where it is decided. This page never
 * decides anything itself: decisions stay on their pages under their approval
 * bindings, so nothing here can approve on a person's behalf.
 */
export function ApprovalsPage() {
  const { user, loading } = useAuth()
  const [data, setData] = useState<PendingGates | null>(null)
  const [items, setItems] = useState<PendingGate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [kind, setKind] = useState<string>('all')

  // Only the newest request may update the inbox: switching types or refreshing
  // supersedes any request (including a pending Load more) still in flight.
  const latest = useRef(0)
  // Which gate type the listed items belong to: a list is only shown under the
  // chip it was loaded for, never under a newly selected one that failed to load.
  const [loadedKind, setLoadedKind] = useState<string | null>(null)
  const [lastAttempt, setLastAttempt] = useState<{ kind: string; offset: number }>({ kind: 'all', offset: 0 })

  // The server filters by type before paging, so every advertised category loads.
  async function load(nextKind = kind, offset = 0) {
    const mine = ++latest.current
    setLastAttempt({ kind: nextKind, offset })
    setBusy(true)
    setError(null)
    if (offset === 0 && nextKind !== loadedKind) {
      setItems([])
      setLoadedKind(null)
    }
    try {
      const page = await fetchPendingGates(nextKind === 'all' ? null : nextKind, offset)
      if (mine !== latest.current) return
      setData(page)
      setItems((prev) => (offset === 0 ? page.items : [...prev, ...page.items]))
      setLoadedKind(nextKind)
    } catch (err) {
      if (mine !== latest.current) return
      setError(err instanceof Error ? err.message : 'Failed to load pending approvals')
    } finally {
      if (mine === latest.current) setBusy(false)
    }
  }

  function choose(next: string) {
    setKind(next)
    void load(next, 0)
  }

  useEffect(() => {
    if (user) void load('all', 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  if (!loading && !user) return <Navigate to="/signin" replace />

  const kinds = Object.entries(data?.by_kind ?? {}).sort((a, b) => b[1] - a[1])

  return (
    <AppChrome
      trailing={
        <button
          type="button"
          onClick={() => void load(kind, 0)}
          className="inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-3 py-2 text-sm text-[var(--color-mist)] hover:text-[var(--color-foam)]"
        >
          <RefreshCw aria-hidden="true" className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      }
    >
      <main className="afos-page space-y-6">
        <header>
          <h1 className="afos-page-title">Approvals</h1>
          <p className="afos-page-lead">
            Every decision agents are waiting on a named person for: handoff decisions, merge
            approvals, outcome reviews, budget blocks, and skill reviews. Each one opens where it is
            decided, under its own approval binding.
          </p>
        </header>

        {error && (
          <ErrorState
            message={lastAttempt.offset > 0 ? `Could not load more: ${error}` : error}
            onRetry={() => void load(lastAttempt.kind, lastAttempt.offset)}
          />
        )}
        {!data && !error && <LoadingState label="Loading pending approvals" />}

        {data && (
          <>
            <UnavailableNotice sources={data.unavailable} />
            <CoverageNotice incomplete={data.incomplete} restricted={data.restricted} />
            <div className="grid gap-4 sm:grid-cols-3">
              <StatTile
                emphasis
                label="Waiting on a person"
                value={data.total}
                icon={<ShieldCheck aria-hidden="true" className="h-5 w-5 text-[var(--color-amber)]" />}
              />
              <StatTile
                label={kind === 'all' ? 'Longest wait' : 'Longest wait (this type)'}
                value={items[0] ? waited(items[0].since) : '—'}
                hint={items[0]?.label}
                icon={<Clock aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
              />
              <StatTile label="Gate types open" value={kinds.length} />
            </div>

            {kinds.length > 0 && (
              <div role="group" aria-label="Filter by gate type" className="flex flex-wrap gap-2">
                <FilterChip active={kind === 'all'} onClick={() => choose('all')}>
                  All · {data.total}
                </FilterChip>
                {kinds.map(([k, n]) => (
                  <FilterChip key={k} active={kind === k} onClick={() => choose(k)}>
                    {data.kinds[k]?.label ?? k} · {n}
                  </FilterChip>
                ))}
              </div>
            )}

            {loadedKind !== kind ? (
              busy ? (
                <LoadingState label="Loading gates of this type" />
              ) : null
            ) : items.length === 0 ? (
              <EmptyState>
                {data.total === 0
                  ? 'Nothing is waiting on a person right now. New gates appear here as agents reach them.'
                  : 'No gates of this type.'}
              </EmptyState>
            ) : (
              <ul className="space-y-3">
                {items.map((g) => (
                  <li key={g.id}>
                    <Link
                      to={g.href}
                      className="glass flex flex-wrap items-center justify-between gap-4 rounded-3xl px-5 py-4 transition hover:bg-white/5"
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-amber)]">
                          {g.label}
                        </div>
                        <div className="mt-1 truncate text-base font-semibold text-[var(--color-foam)]">
                          {g.title}
                        </div>
                        <div className="mt-1 text-sm text-[var(--color-mist)]">
                          Decided by: {g.who} · <span className="font-mono text-xs">{g.subject}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-[var(--color-mist)]">
                        <span className="tabular-nums">waiting {waited(g.since)}</span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-line)] px-3 py-1.5 text-[var(--color-foam)]">
                          Decide <ArrowRight aria-hidden="true" className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {loadedKind === kind && data.next_offset != null && (
              <div className="flex items-center justify-between gap-3 text-sm text-[var(--color-mist)]">
                <span>
                  Showing the oldest {items.length} of {data.matching}.
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void load(kind, data.next_offset ?? 0)}
                  className="rounded-full border border-[var(--color-line)] px-4 py-2 text-[var(--color-foam)] hover:bg-white/5 disabled:opacity-50"
                >
                  {busy ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </AppChrome>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm transition ${
        active
          ? 'border-[var(--color-amber)] bg-[rgba(255,179,71,0.12)] text-[var(--color-foam)]'
          : 'border-[var(--color-line)] text-[var(--color-mist)] hover:text-[var(--color-foam)]'
      }`}
    >
      {children}
    </button>
  )
}
